import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { EMPTY, Observable, Subject, Subscription, forkJoin, of } from "rxjs";
import { catchError, finalize, map, switchMap, tap } from "rxjs/operators";
import type {
  MeetBackgroundCorner,
  MeetBackgroundFormControls,
  MeetBackgroundLayoutVariant,
  MeetBackgroundPreviewBindings,
  MeetCornerField,
  SavedTemplateState,
} from "../meet-background.interface";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { NameInputDialogComponent } from "../../shared/name-input-dialog/name-input-dialog.component";
import { MeetBackgroundService } from "../meet-background.service";
import { DigitalCardService } from "../../digital-cards/digital-card.service";
import type { DigitalCardProfileItem } from "../../digital-cards/digital-cards.interface";
import type { EmployeeWithBrand } from "../../digital-cards/card-digital.interface";
import { defaultDigitalCardProfileId } from "../../digital-cards/digital-card-profile.util";
import {
  MEET_BACKGROUND_CORNERS,
  MEET_LAYOUT,
  meetCornersExcept,
  meetEditorFormValuesFromItem,
  meetFallbackQrTarget,
  meetFixOverlappingCorner,
  meetMediaImageLabel,
  meetPreviewEmployeeName,
  meetPreviewJobTitle,
  meetPreviewLogoUrl,
  meetSettingsFromFormValue,
} from "../meet-background.util";
import { CompanyService } from "../../company/company.service";
import { ImagesService } from "../../media/images.service";
import type { Image } from "../../media/media.interface";
import type { EmployeePickerItem } from "../../employees/employees.interface";
import { EmployeesService } from "../../employees/employees.service";
import {
  BRAND_CANVAS_FONT_PRESETS,
  BRAND_FONT_PRESET_ID,
} from "../../shared/font-presets";

@Component({
  selector: "app-meet-background-editor",
  templateUrl: "./meet-background-editor.component.html",
  styleUrls: ["./meet-background-editor.component.scss"],
  standalone: false,
})
export class MeetBackgroundEditorComponent implements OnInit, OnDestroy {
  meetBackgroundId: string | null = null;
  isCreateMode = false;
  loading = true;
  previewSettled = false;
  saving = false;
  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();
  previewEmployeeLoading = false;
  employeePickerReady = false;
  previewQrTarget: string | null = null;
  digitalCardTemplates: DigitalCardProfileItem[] = [];
  readonly digitalCardTemplateLabel = (template: DigitalCardProfileItem) =>
    template.displayName || template.profileId || "";
  readonly digitalCardTemplateValue = (template: DigitalCardProfileItem) =>
    template.profileId;
  readonly mediaLabel = (image: Image) => image.name || "";
  readonly mediaValue = (image: Image) => image.imageUrl;
  readonly mediaImage = (image: Image) => image.imageUrl;
  companyLogoUrl: string | null = null;
  companyFontFamily = "";
  companyFontFileUrl: string | null = null;
  mediaImages: Image[] = [];
  mediaImagesLoading = false;
  previewEmployee: EmployeePickerItem | null = null;
  previewEmployeeData: EmployeeWithBrand | null = null;
  private readonly builtInFontPresets = BRAND_CANVAS_FONT_PRESETS;
  private readonly subscriptions = new Subscription();
  private readonly previewEmail$ = new Subject<string>();
  private previewQrSub?: Subscription;
  private pendingFontSettings: {
    fontName?: string;
    fontFamily?: string;
    fontCssUrl?: string | null;
  } | null = null;

  readonly logoScaleLimits = MEET_LAYOUT.logoScale;

  form = new FormGroup<MeetBackgroundFormControls>({
    name: new FormControl<string>("", {
      nonNullable: true,
    }),
    digitalCardProfileId: new FormControl<string | null>(null),
    qrCaption: new FormControl<string>("Scan to save my contact", {
      nonNullable: true,
    }),
    showEmployeeName: new FormControl<boolean>(true, { nonNullable: true }),
    showJobTitle: new FormControl<boolean>(true, { nonNullable: true }),
    showLogo: new FormControl<boolean>(true, { nonNullable: true }),
    logoUrl: new FormControl<string>("", { nonNullable: true }),
    fontPreset: new FormControl<string>("default", { nonNullable: true }),
    layoutVariant: new FormControl<MeetBackgroundLayoutVariant>("corner", {
      nonNullable: true,
    }),
    barWidth: new FormControl<"compact" | "full">("compact", {
      nonNullable: true,
    }),
    corner: new FormControl<MeetBackgroundCorner>("bottom-right", {
      nonNullable: true,
    }),
    logoCorner: new FormControl<MeetBackgroundCorner>("top-left", {
      nonNullable: true,
    }),
    opacity: new FormControl<number>(0.92, { nonNullable: true }),
    qrBackgroundColor: new FormControl<string>("#f5f5f5", {
      nonNullable: true,
    }),
    marginPx: new FormControl<number>(24, { nonNullable: true }),
    sizeRatio: new FormControl<number>(MEET_LAYOUT.scale.default, {
      nonNullable: true,
    }),
    logoOpacity: new FormControl<number>(0.92, { nonNullable: true }),
    logoBackgroundColor: new FormControl<string>("#ffffff", {
      nonNullable: true,
    }),
    logoMarginPx: new FormControl<number>(24, { nonNullable: true }),
    logoSizeRatio: new FormControl<number>(MEET_LAYOUT.logoScale.default, {
      nonNullable: true,
    }),
  });

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private meetBackgroundService: MeetBackgroundService,
    private digitalCardService: DigitalCardService,
    private employeesService: EmployeesService,
    private companyService: CompanyService,
    private imagesService: ImagesService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.meetBackgroundId =
      this.route.snapshot.paramMap.get("meetBackgroundId");
    this.isCreateMode = this.meetBackgroundId == null;
    this.initPreviewEmployeeStream();
    this.loadMediaImages();
    if (this.isCreateMode) {
      this.bootstrapCreatePage();
    } else {
      this.bootstrapEditPage();
    }
  }

  ngOnDestroy(): void {
    this.previewQrSub?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  get pageReady(): boolean {
    return !this.loading && this.previewSettled;
  }

  get canSave(): boolean {
    return true;
  }

  get hasQrDigitalCard(): boolean {
    return !!this.form.controls.digitalCardProfileId.value?.trim();
  }

  get previewBindings(): MeetBackgroundPreviewBindings {
    const value = this.form.getRawValue();
    const hasQr = this.hasQrDigitalCard;
    const selected =
      this.fontPresets.find((font) => font.id === value.fontPreset) ??
      this.fontPresets[0];
    return {
      layoutVariant: value.layoutVariant,
      barWidth: value.barWidth,
      corner: value.corner,
      logoCorner: value.logoCorner,
      showQrCaption: hasQr && !!value.qrCaption.trim(),
      qrCaption: value.qrCaption,
      showEmployeeName: hasQr && value.showEmployeeName,
      previewEmployeeName: meetPreviewEmployeeName(this.previewEmployeeData),
      showJobTitle: hasQr && value.showJobTitle,
      previewJobTitle: meetPreviewJobTitle(this.previewEmployeeData),
      showLogo: value.showLogo,
      logoUrl: meetPreviewLogoUrl(
        value.showLogo,
        value.logoUrl,
        this.companyLogoUrl,
      ),
      qrData: hasQr ? this.previewQrTarget : null,
      opacity: value.opacity,
      qrBackgroundColor: value.qrBackgroundColor,
      logoOpacity: value.logoOpacity,
      logoBackgroundColor: value.logoBackgroundColor,
      marginPx: value.marginPx,
      logoMarginPx: value.logoMarginPx,
      sizeRatio: value.sizeRatio,
      logoSizeRatio: value.logoSizeRatio,
      fontFamily: selected.fontFamily,
      fontCssUrl:
        selected.id === BRAND_FONT_PRESET_ID
          ? null
          : (selected.fontCssUrl ?? null),
      companyFontFileUrl:
        selected.id === BRAND_FONT_PRESET_ID ? this.companyFontFileUrl : null,
      loading:
        this.saving ||
        this.previewEmployeeLoading ||
        (!!this.form.controls.digitalCardProfileId.value?.trim() &&
          !this.employeePickerReady),
      renderedImageUrl: null,
    };
  }

  get companyBrandFontReady(): boolean {
    return (
      this.companyFontFamily.trim().length > 0 &&
      (this.companyFontFileUrl?.trim().length ?? 0) > 0
    );
  }

  get fontPresets(): Array<{
    id: string;
    label: string;
    fontFamily: string;
    fontCssUrl?: string;
  }> {
    if (!this.companyBrandFontReady) {
      return this.builtInFontPresets;
    }
    return [
      {
        id: BRAND_FONT_PRESET_ID,
        label: this.companyFontFamily.trim(),
        fontFamily: `"${this.companyFontFamily.replace(/"/g, "")}"`,
      },
      ...this.builtInFontPresets,
    ];
  }

  get pickerTemplateContextId(): string | null {
    const digitalCardProfileId = (
      this.form.controls.digitalCardProfileId.value ?? ""
    ).trim();
    return digitalCardProfileId.length > 0 ? digitalCardProfileId : null;
  }

  get nameInvalid(): boolean {
    const control = this.form.controls.name;
    return control.invalid && this.errorStateMatcher.submitAttempted;
  }

  get isCornerLayout(): boolean {
    return this.form.controls.layoutVariant.value === "corner";
  }

  get isBottomBarLayout(): boolean {
    return this.form.controls.layoutVariant.value === "bottom-bar";
  }

  get isFullOverlayLayout(): boolean {
    return this.form.controls.layoutVariant.value === "full-overlay";
  }

  qrCornerOptions(): MeetBackgroundCorner[] {
    return meetCornersExcept(this.form.controls.logoCorner.value);
  }

  logoCornerOptions(): MeetBackgroundCorner[] {
    if (this.isFullOverlayLayout) {
      return MEET_BACKGROUND_CORNERS;
    }
    return meetCornersExcept(this.form.controls.corner.value);
  }

  onPreviewEmployeeChange(employee: EmployeePickerItem | null): void {
    this.previewEmployee = employee;
    this.employeePickerReady = true;
    this.previewEmail$.next(employee?.email?.trim().toLowerCase() ?? "");
    this.refreshPreviewQrTarget();
  }

  onEmployeePickerInitialResolved(): void {
    this.employeePickerReady = true;
    this.refreshPreviewQrTarget();
  }

  onCornerChange(changed: MeetCornerField): void {
    if (this.isFullOverlayLayout) {
      return;
    }
    this.form.patchValue(
      meetFixOverlappingCorner(
        this.form.controls.corner.value,
        this.form.controls.logoCorner.value,
        changed,
      ),
    );
  }

  onPlateColorChange(
    controlName: "qrBackgroundColor" | "logoBackgroundColor",
    value: string,
  ): void {
    this.form.patchValue({ [controlName]: value });
  }

  save(): void {
    if (this.saving) {
      return;
    }
    if (!this.form.controls.name.value.trim()) {
      this.showNameInputDialog();
      return;
    }
    this.persistTemplate();
  }

  private showNameInputDialog(): void {
    const dialogRef = this.dialog.open(NameInputDialogComponent, {
      width: "500px",
      data: {
        title: this.translate.instant("meetBackground.enterName") as string,
        placeholder: this.translate.instant("footers.name") as string,
        currentName: this.form.controls.name.value || "",
        actionType: "save" as const,
        saveButtonText: this.translate.instant("save") as string,
      },
    });
    dialogRef
      .afterClosed()
      .subscribe((result: { name?: string; action?: string } | undefined) => {
        if (!result?.name?.trim()) {
          return;
        }
        this.form.controls.name.setValue(result.name.trim());
        this.persistTemplate();
      });
  }

  back(): void {
    void this.router.navigate(["/backgrounds/meets"]);
  }

  onDigitalCardProfileChange(): void {
    if (!this.pageReady) {
      return;
    }
    this.employeePickerReady = false;
    this.previewEmployee = null;
    this.previewEmployeeData = null;
    if (!this.hasQrDigitalCard) {
      this.previewQrTarget = null;
      return;
    }
    this.previewQrTarget = null;
  }

  companyLogoTriggerLabel(url: string): string {
    return meetMediaImageLabel(url, this.mediaImages);
  }

  private bootstrapCreatePage(): void {
    this.form.patchValue({
      qrCaption: "Scan to save my contact",
      showEmployeeName: true,
      showJobTitle: true,
      showLogo: true,
    });
    this.loadPageBootstrap();
  }

  private bootstrapEditPage(): void {
    const meetBackgroundId = this.meetBackgroundId?.trim();
    if (!meetBackgroundId) {
      this.finishPageBootstrap();
      return;
    }
    this.loadPageBootstrap(meetBackgroundId);
  }

  private loadPageBootstrap(meetBackgroundId?: string): void {
    this.loading = true;
    this.previewSettled = false;

    const savedTemplate$: Observable<SavedTemplateState> = meetBackgroundId
      ? this.meetBackgroundService.getOne(meetBackgroundId).pipe(
          tap((item) => {
            this.form.patchValue(meetEditorFormValuesFromItem(item));
            this.pendingFontSettings = {
              fontName: item.settings?.fontName,
              fontFamily: item.settings?.fontFamily,
              fontCssUrl: item.settings?.fontCssUrl,
            };
            this.onCornerChange("corner");
            this.onCornerChange("logoCorner");
          }),
          map((item): SavedTemplateState => ({
            savedProfileId: item.digitalCardProfileId?.trim() || null,
            hasSavedTemplate: true,
          })),
          catchError(() =>
            of<SavedTemplateState>({
              savedProfileId: null,
              hasSavedTemplate: true,
            }),
          ),
        )
      : of<SavedTemplateState>({
          savedProfileId: null,
          hasSavedTemplate: false,
        });

    forkJoin({
      company: this.companyService.get().pipe(catchError(() => of(null))),
      templates: this.digitalCardService
        .getProfiles({ templatesOnly: true })
        .pipe(
          catchError(() => of({ profiles: [] as DigitalCardProfileItem[] })),
        ),
      savedTemplate: savedTemplate$,
    })
      .pipe(
        switchMap(({ company, templates, savedTemplate }) => {
          this.companyLogoUrl = company?.photoUrl
            ? `${company.photoUrl}?t=${Date.now()}`
            : null;
          this.companyFontFamily =
            (company?.brandFontFamily ?? "").trim() ||
            (company?.brandFontFileName ?? "").trim().replace(/\.[^.]+$/, "") ||
            "";
          this.companyFontFileUrl =
            (company?.brandFontFileUrl ?? "").trim() || null;
          if (this.pendingFontSettings) {
            const fontCssUrl = (
              this.pendingFontSettings.fontCssUrl ?? ""
            ).trim();
            const fontFamily = (
              this.pendingFontSettings.fontFamily ?? ""
            ).trim();
            const fontName = (this.pendingFontSettings.fontName ?? "").trim();
            const familyPrimary = fontFamily
              .split(",")[0]
              ?.trim()
              .replace(/^["']|["']$/g, "");
            const hasExplicitFont = !!(fontCssUrl || fontFamily || fontName);
            let fontPresetId = "default";
            if (
              this.companyBrandFontReady &&
              (!hasExplicitFont ||
                (!fontCssUrl &&
                  familyPrimary.toLowerCase() ===
                    this.companyFontFamily.toLowerCase()))
            ) {
              fontPresetId = BRAND_FONT_PRESET_ID;
            } else if (fontName) {
              const byName = this.builtInFontPresets.find(
                (font) =>
                  font.label.trim().toLowerCase() === fontName.toLowerCase(),
              );
              if (byName) {
                fontPresetId = byName.id;
              }
            }
            if (fontPresetId === "default" && fontFamily) {
              const byFamily = this.builtInFontPresets.find(
                (font) =>
                  font.fontFamily.trim().toLowerCase() ===
                  fontFamily.toLowerCase(),
              );
              if (byFamily) {
                fontPresetId = byFamily.id;
              }
            }
            this.form.patchValue(
              { fontPreset: fontPresetId },
              { emitEvent: false },
            );
            this.pendingFontSettings = null;
          }
          this.digitalCardTemplates = templates.profiles ?? [];
          const profileId: string | null = savedTemplate.hasSavedTemplate
            ? savedTemplate.savedProfileId
            : (savedTemplate.savedProfileId ??
              defaultDigitalCardProfileId(this.digitalCardTemplates));
          if (!savedTemplate.hasSavedTemplate && profileId) {
            this.form.patchValue(
              { digitalCardProfileId: profileId },
              { emitEvent: false },
            );
          }
          const qrProfileId =
            this.form.controls.digitalCardProfileId.value?.trim() || null;
          return forkJoin({
            qr: this.fetchPreviewQrTarget(qrProfileId),
            preview: this.bootstrapPreviewEmployee(),
          });
        }),
      )
      .subscribe({
        next: ({ qr }) => {
          this.previewQrTarget = qr;
          this.finishPageBootstrap();
        },
        error: () => {
          this.previewQrTarget = this.hasQrDigitalCard
            ? meetFallbackQrTarget()
            : null;
          this.finishPageBootstrap();
        },
      });
  }

  private finishPageBootstrap(): void {
    this.previewSettled = true;
    this.loading = false;
  }

  private bootstrapPreviewEmployee(): Observable<void> {
    return this.employeesService
      .getEmployeesPicker({ pager: { page: 0, limit: 1 } })
      .pipe(
        map((response) => response?.data?.[0] ?? null),
        catchError(() => of<EmployeePickerItem | null>(null)),
        switchMap((employee): Observable<void> => {
          this.previewEmployee = employee;
          const email = employee?.email?.trim().toLowerCase() ?? "";
          const value = this.form.getRawValue();
          if (!email || (!value.showEmployeeName && !value.showJobTitle)) {
            this.previewEmployeeData = null;
            return of(void 0);
          }
          return this.digitalCardService.getPreviewEmployee(email).pipe(
            map((response) => {
              this.previewEmployeeData = response.employee;
            }),
            catchError(() => {
              this.previewEmployeeData = null;
              return EMPTY;
            }),
            map((): void => undefined),
          );
        }),
      );
  }

  private fetchPreviewQrTarget(
    profileId: string | null,
  ): Observable<string | null> {
    if (!profileId) {
      return of(null);
    }
    return this.meetBackgroundService.getPreviewQrTarget(profileId).pipe(
      map((response) => response.qrTarget?.trim() || meetFallbackQrTarget()),
      catchError(() => of(meetFallbackQrTarget())),
    );
  }

  private persistTemplate(): void {
    if (this.saving) {
      return;
    }
    const meetBackgroundId = this.meetBackgroundId?.trim();
    if (!this.isCreateMode && !meetBackgroundId) {
      return;
    }
    this.saving = true;
    const value = this.form.getRawValue();
    const selected =
      this.fontPresets.find((font) => font.id === value.fontPreset) ??
      this.fontPresets[0];
    const payload = {
      name: value.name.trim(),
      digitalCardProfileId: value.digitalCardProfileId || null,
      settings: meetSettingsFromFormValue({
        ...value,
        fontName: selected.label,
        fontFamily: selected.fontFamily,
        fontCssUrl:
          selected.id === BRAND_FONT_PRESET_ID
            ? null
            : (selected.fontCssUrl ?? null),
      }),
    };
    const request$ = this.isCreateMode
      ? this.meetBackgroundService.create(payload)
      : this.meetBackgroundService.update(meetBackgroundId, payload);

    request$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant("meetBackground.saved") as string,
        );
        void this.router.navigate(["/backgrounds/meets"]);
      },
    });
  }

  private refreshPreviewQrTarget(): void {
    const profileId =
      this.form.controls.digitalCardProfileId.value?.trim() || null;
    if (!profileId) {
      this.previewQrTarget = null;
      return;
    }
    if (!this.employeePickerReady) {
      return;
    }
    this.previewQrSub?.unsubscribe();
    this.previewQrSub = this.fetchPreviewQrTarget(profileId).subscribe({
      next: (qr) => {
        this.previewQrTarget = qr;
      },
    });
  }

  private loadMediaImages(): void {
    this.mediaImagesLoading = true;
    this.imagesService.getImages().subscribe({
      next: (list) => {
        this.mediaImages = Array.isArray(list) ? list : [];
        this.mediaImagesLoading = false;
      },
      error: () => {
        this.mediaImages = [];
        this.mediaImagesLoading = false;
      },
    });
  }

  private initPreviewEmployeeStream(): void {
    this.subscriptions.add(
      this.previewEmail$
        .pipe(
          tap(() => {
            this.previewEmployeeData = null;
            if (this.pageReady) {
              this.previewEmployeeLoading = true;
            }
          }),
          switchMap((email) =>
            email
              ? this.digitalCardService.getPreviewEmployee(email).pipe(
                  map((response) => response.employee),
                  catchError(() => of<EmployeeWithBrand | null>(null)),
                )
              : of<EmployeeWithBrand | null>(null),
          ),
          tap((employee) => {
            this.previewEmployeeData = employee;
            this.previewEmployeeLoading = false;
          }),
        )
        .subscribe(),
    );
  }
}
